import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function validatePassword(pw: string) {
  const errors: string[] = [];
  if (pw.length < 6 || pw.length > 12) errors.push('6-12 characters');
  if (!/\d/.test(pw)) errors.push('at least 1 number');
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw)) errors.push('at least 1 symbol');
  return errors;
}

const ResetPasswordPage: React.FC = () => {
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const { API_BASE_URL } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pwErrors, setPwErrors] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'idle' | 'working' | 'ok'>('idle');

  useEffect(() => { document.title = 'Reset password — Sedra'; }, []);

  const onPassword = (v: string) => {
    setPassword(v);
    setPwErrors(v ? validatePassword(v) : []);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('Missing or invalid token.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (pwErrors.length) { setError('Please fix password requirements.'); return; }

    setStatus('working');
    try {
      const r = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.error || 'Reset failed');
      }
      setStatus('ok');
      setTimeout(() => navigate('/login'), 2000);
    } catch (e: any) {
      setError(e?.message || 'Reset failed');
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-md px-6 pt-16">
        <header className="text-center">
          <img src="/logo-color.svg" alt="" aria-hidden="true" className="mx-auto h-8 w-auto" />
          <h1 className="display-hero mt-6 text-3xl font-extrabold tracking-tight text-gray-900">Set a new password</h1>
          <p className="mt-2 text-sm text-gray-600">Reset links are valid for 1 hour.</p>
        </header>

        <main id="main" className="mt-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 elev-1">
            {status === 'ok' ? (
              <div className="space-y-3 text-center">
                <p className="text-green-700">Your password has been updated.</p>
                <Link to="/login" className="text-sedra hover:underline">Return to sign in</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">New password</label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={e => onPassword(e.target.value)}
                    className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                  />
                  <p className={`mt-1 text-xs ${pwErrors.length ? 'text-red-600' : 'text-gray-500'}`}>
                    {pwErrors.length ? `Requirements: ${pwErrors.join(', ')}` : '6-12 characters, 1 number, 1 symbol'}
                  </p>
                </div>

                <div>
                  <label htmlFor="confirm" className="block text-sm font-medium text-gray-700">Confirm password</label>
                  <input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                  />
                  {!!confirm && password !== confirm && (
                    <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
                  )}
                </div>

                {error && (
                  <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'working'}
                  aria-busy={status === 'working'}
                  className="w-full rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-sedra"
                >
                  {status === 'working' ? 'Saving…' : 'Update password'}
                </button>

                <div className="text-center">
                  <Link to="/login" className="text-sm text-sedra hover:underline">Back to sign in</Link>
                </div>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 